import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { addBusinessDays, fromIso, toIso } from '../utils/businessDate';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import type { ColDef } from 'ag-grid-community';
import { getTenantToday } from '../utils/dateUtils';
import {
  fetchClassifications,
  buildPivotRows,
  loadStoredPivotDims,
  storePivotDims,
  PIVOT_DIMENSION_LABELS,
  type PivotDimension,
  type SecurityClassification,
} from './classificationPivot';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Activity,
  TrendingUp,
  RefreshCcw,
  Clock,
  ShieldCheck,
  Zap,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  Calendar,
  Package,
  TrendingDown
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  AxiomButton,
  AxiomDateField,
  AxiomFieldLabel,
  AxiomInput,
  AxiomMetricCard,
  AxiomMetricGrid,
  AxiomSelect,
  AxiomStatusBadge,
  InstitutionalDataGrid,
  ObjectDetailDrawer,
} from './shared';

// ============== Animations ==============
const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const SecurityCell = styled.div`
  width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.18rem;
  overflow: hidden;
  line-height: 1.15;
`;

const SecurityName = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #f8fafc;
  font-weight: 800;
  font-size: 0.78rem;
`;

const PortfolioName = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 650;
`;

const GroupCell = styled.div<{ $depth: number; $unclassified?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  height: 100%;
  min-width: 0;
  cursor: pointer;
  color: ${p => p.$unclassified ? '#fbbf24' : '#e2e8f0'};
  padding-left: ${p => p.$depth * 1.25}rem;

  &::before {
    content: '';
    display: ${p => p.$depth > 0 ? 'block' : 'none'};
    position: absolute;
    left: ${p => Math.max(0.35, p.$depth * 1.25 - 0.55)}rem;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(148, 163, 184, 0.18);
  }
`;

const GroupCaret = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.8);
  color: #93c5fd;
  font-size: 0.72rem;
  line-height: 1;
`;

const GroupMeta = styled.span`
  color: #94a3b8;
  font-size: 0.66rem;
  font-weight: 800;
  white-space: nowrap;
`;

const GroupTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 900;
`;

const PivotChip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => p.$active ? 'rgba(129, 140, 248, 0.6)' : 'rgba(255, 255, 255, 0.12)'};
  border-radius: 10px;
  background: ${p => p.$active ? 'rgba(129, 140, 248, 0.25)' : 'transparent'};
  color: ${p => p.$active ? '#c7d2fe' : '#94a3b8'};
  padding: 0.4rem 0.7rem;
  font-size: 0.68rem;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #e2e8f0;
    border-color: rgba(255, 255, 255, 0.25);
  }

  sup {
    margin-right: 0.25rem;
    color: #818cf8;
  }
`;

// ============== Interfaces ==============
interface Position {
  id: number;
  portfolioId: number;
  portfolioName: string;
  securityId: number;
  securityName: string;
  cusip: string;
  assetClass: string;
  quantity: number;
  parAmount: number;
  commitmentAmount?: number;
  drawnAmount?: number;
  unfundedCommitment?: number;
  marketValue: number;
  costBasis: number;
  unrealizedPnl: number;
  realizedPnl: number;
  asOfDate: string;
  settleDate?: string;
  validFrom?: string;
  validTo?: string;
  systemFrom?: string;
  systemTo?: string;
}

interface TaxLot {
  id: number;
  lotNumber: number;
  lotReference: string;
  lotDate: string;
  settleDate: string;
  sourceType: string;
  lotStatus: string;
  originalQuantity: number;
  currentQuantity: number;
  originalParAmount: number;
  currentParAmount: number;
  acquisitionPrice: number;
  originalAcquisitionCost?: number;
  acquisitionCost: number;
  amortizedCost?: number;
  accumulatedAmortization?: number;
  unrealizedGainLoss: number;
  unrealizedGainLossPct: number;
  createdAt: string;
}

type PositionDateType = 'trade' | 'settlement';
type IBORViewMode = 'current' | 'replay';
type ReplayPreset = 'worldThen' | 'knownThen' | 'todayReconstructed' | null;

const toDateTimeLocal = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,%]/g, '').replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sumLotCurrentQuantity = (lots: TaxLot[]): number =>
  lots.reduce((sum, lot) => sum + normalizeNumber(lot.currentQuantity ?? lot.currentParAmount), 0);

const replayLotsMismatchPosition = (lots: TaxLot[], position: Position): boolean => {
  if (!lots.length) return true;
  const expectedQuantity = normalizeNumber(position.quantity || position.parAmount);
  if (!expectedQuantity) return false;
  return Math.abs(sumLotCurrentQuantity(lots) - expectedQuantity) > 0.01;
};

const buildReplayPositionLot = (position: Position, asOfDate: string): TaxLot => {
  const quantity = normalizeNumber(position.quantity || position.parAmount);
  const costBasis = normalizeNumber(position.costBasis);
  const marketValue = normalizeNumber(position.marketValue);
  return {
    id: -Math.abs(position.id || position.securityId || 1),
    lotNumber: 1,
    lotReference: 'REPLAY_POSITION',
    lotDate: asOfDate,
    settleDate: position.settleDate || asOfDate,
    sourceType: 'IBOR_REPLAY',
    lotStatus: 'OPEN',
    originalQuantity: quantity,
    currentQuantity: quantity,
    originalParAmount: quantity,
    currentParAmount: quantity,
    acquisitionPrice: quantity ? (costBasis / quantity) * 100 : 0,
    originalAcquisitionCost: costBasis,
    acquisitionCost: costBasis,
    amortizedCost: costBasis,
    accumulatedAmortization: 0,
    unrealizedGainLoss: marketValue - costBasis,
    unrealizedGainLossPct: costBasis ? ((marketValue - costBasis) / costBasis) * 100 : 0,
    createdAt: position.systemFrom || position.validFrom || '',
  };
};

const IBORDashboardScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialPositionQuery = searchParams.get('query') || '';
  const navigate = useNavigate();
  // Core state
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [summary, setSummary] = useState<any>(null);
  const [cashBalances, setCashBalances] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Bi-temporal state
  const [iborViewMode, setIborViewMode] = useState<IBORViewMode>('current');
  const [positionDateType, setPositionDateType] = useState<PositionDateType>('trade');
  const [asOfDate, setAsOfDate] = useState<string>(getTenantToday());
  const [validAsOf, setValidAsOf] = useState<string>(getTenantToday());
  const [knowledgeAsOf, setKnowledgeAsOf] = useState<string>(toDateTimeLocal(new Date()));
  const [replayPreset, setReplayPreset] = useState<ReplayPreset>(null);
  const [replaySummary, setReplaySummary] = useState<any>(null);

  // Positions state
  const [positions, setPositions] = useState<Position[]>([]);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);

  // Tax lot drill-down state
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [taxLots, setTaxLots] = useState<TaxLot[]>([]);
  const [isLoadingLots, setIsLoadingLots] = useState(false);
  const [showLotModal, setShowLotModal] = useState(false);

  // Classification pivot state (FRD Phase 2). pivotDims is click-ordered:
  // the hierarchy is dims[0] → dims[1] → ... → Security. The last-used
  // grouping is restored so the IBOR menu reopens in the same view.
  const [pivotDims, setPivotDims] = useState<PivotDimension[]>(loadStoredPivotDims);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [classifications, setClassifications] = useState<Map<number, SecurityClassification>>(new Map());

  useEffect(() => { storePivotDims(pivotDims); }, [pivotDims]);

  // Fetch positions with bi-temporal support
  const fetchPositions = useCallback(async () => {
    setIsLoadingPositions(true);
    try {
      // Pass dateType to backend for proper trade date vs settlement date filtering
      const dateType = positionDateType === 'trade' ? 'TRADE_DATE' : 'SETTLEMENT_DATE';

      const normalizePosition = (p: any) => ({
        ...p,
        assetClass: p.assetClass || p.assetClassName || p.assetSubClassName || null,
        quantity: normalizeNumber(p.quantity),
        parAmount: normalizeNumber(p.parAmount),
        commitmentAmount: normalizeNumber(p.commitmentAmount),
        drawnAmount: normalizeNumber(p.drawnAmount),
        unfundedCommitment: normalizeNumber(p.unfundedCommitment),
        marketValue: normalizeNumber(p.marketValue),
        costBasis: normalizeNumber(p.costBasis),
        unrealizedPnl: normalizeNumber(p.unrealizedPnl),
        realizedPnl: normalizeNumber(p.realizedPnl)
      });

      const params: any = {
        validAsOf: iborViewMode === 'replay' ? validAsOf : asOfDate,
        knowledgeAsOf: iborViewMode === 'replay' ? knowledgeAsOf : 'now',
        basis: dateType
      };
      if (selectedPortfolioId) params.portfolioId = selectedPortfolioId;

      const response = await axios.get('/api/ibor/replay', { params });
      const raw = response.data?.positions || [];
      const data = (Array.isArray(raw) ? raw : []).map(normalizePosition);
      setReplaySummary(response.data?.summary || null);
      setPositions(data);
    } catch (error) {
      console.error('Error fetching positions:', error);
      setPositions([]);
    } finally {
      setIsLoadingPositions(false);
    }
  }, [selectedPortfolioId, asOfDate, positionDateType, iborViewMode, validAsOf, knowledgeAsOf]);

  // Resolve classifications for the loaded securities at the same bi-temporal
  // coordinates as the register, so grouped rows replay history correctly.
  useEffect(() => {
    if (positions.length === 0) {
      setClassifications(new Map());
      return;
    }
    const ids = Array.from(new Set(positions.map(p => p.securityId).filter(Boolean)));
    let cancelled = false;
    fetchClassifications(
      ids,
      iborViewMode === 'replay' ? validAsOf : asOfDate,
      iborViewMode === 'replay' ? knowledgeAsOf : ''
    )
      .then(map => { if (!cancelled) setClassifications(map); })
      .catch(error => {
        console.error('Error resolving classifications:', error);
        if (!cancelled) setClassifications(new Map());
      });
    return () => { cancelled = true; };
  }, [positions, iborViewMode, validAsOf, asOfDate, knowledgeAsOf]);

  const togglePivotDim = useCallback((dim: PivotDimension) => {
    setExpandedGroups(new Set());
    setPivotDims(prev => prev.includes(dim) ? prev.filter(d => d !== dim) : [...prev, dim]);
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Fetch tax lots for a position
  const fetchTaxLots = useCallback(async (position: Position) => {
    setIsLoadingLots(true);
    setSelectedPosition(position);
    setShowLotModal(true);
    try {
      // Try the lot repository endpoint with date type
      const dateType = positionDateType === 'trade' ? 'TRADE_DATE' : 'SETTLEMENT_DATE';
      const lotAsOfDate = iborViewMode === 'replay' ? validAsOf : asOfDate;
      const params: Record<string, string> = { asOfDate: lotAsOfDate, dateType };
      if (iborViewMode === 'replay') params.knowledgeAsOf = knowledgeAsOf;
      const response = await axios.get(`/api/positions/${position.portfolioId}/${position.securityId}/lots`, {
        params
      });
      const lots = Array.isArray(response.data) ? response.data : [];
      const shouldUseReplayPositionLot =
        iborViewMode === 'replay' &&
        positionDateType === 'trade' &&
        replayLotsMismatchPosition(lots, position);

      setTaxLots(shouldUseReplayPositionLot ? [buildReplayPositionLot(position, lotAsOfDate)] : lots);
    } catch (error) {
      console.error('Error fetching tax lots:', error);
      setTaxLots([]);
    } finally {
      setIsLoadingLots(false);
    }
  }, [asOfDate, iborViewMode, knowledgeAsOf, positionDateType, validAsOf]);

  const fetchIBORData = useCallback(async () => {
    setIsLoading(true);
    try {
      const portfolioRes = await axios.get('/api/dimensions/portfolios');
      const portfolioList = portfolioRes.data?.content || portfolioRes.data || [];
      setPortfolios(portfolioList);

      const summaryParams: any = { asOfDate };
      if (selectedPortfolioId) summaryParams.portfolioId = selectedPortfolioId;
      const summaryRes = await axios.get('/api/ibor/summary', { params: summaryParams });
      setSummary(summaryRes.data);

      // Try cash-balances endpoint first
      let cashData: any[] = [];
      try {
        const cashUrl = selectedPortfolioId ? `/api/ibor/cash-balances/${selectedPortfolioId}` : '/api/ibor/cash-balances';
        const cashRes = await axios.get(cashUrl, { params: { asOfDate } });

        if (Array.isArray(cashRes.data) && cashRes.data.length > 0) {
          cashData = cashRes.data;
        } else if (cashRes.data && (cashRes.data.portfolioName || cashRes.data.balances)) {
          if (cashRes.data.portfolioName) {
            cashData = [cashRes.data];
          } else if (cashRes.data.balances && Object.keys(cashRes.data.balances).length > 0) {
            const portfolio = portfolioList.find((p: any) => p.id === parseInt(selectedPortfolioId));
            cashData = Object.entries(cashRes.data.balances).map(([ccy, bal]) => ({
              portfolioName: portfolio?.portfolioName || `Portfolio ${selectedPortfolioId}`,
              currency: ccy,
              settledBalance: bal,
              projectedBalance: bal
            }));
          }
        }
      } catch (err) {
        console.warn('Cash balances endpoint failed, using fallback');
      }

      // Fallback: If no cash data, use NAV endpoint to get cash from positions
      if (cashData.length === 0) {
        try {
          if (selectedPortfolioId) {
            // Get NAV for specific portfolio
            const navRes = await axios.get(`/api/ibor/nav/${selectedPortfolioId}`, { params: { asOfDate } });
            if (navRes.data && navRes.data.cashTotal) {
              const portfolio = portfolioList.find((p: any) => p.id === parseInt(selectedPortfolioId));
              cashData = [{
                portfolioName: portfolio?.portfolioName || navRes.data.portfolioName || `Portfolio ${selectedPortfolioId}`,
                currency: 'USD',
                settledBalance: navRes.data.cashTotal,
                projectedBalance: navRes.data.cashTotal
              }];
            }
          } else {
            // Get NAV for all portfolios
            const navAllRes = await axios.get('/api/ibor/nav-all', { params: { asOfDate } });
            if (Array.isArray(navAllRes.data)) {
              cashData = navAllRes.data
                .filter((nav: any) => nav.cashTotal !== undefined && nav.cashTotal !== null && nav.cashTotal !== 0)
                .map((nav: any) => ({
                  portfolioId: nav.portfolioId,
                  portfolioName: nav.portfolioName || `Portfolio ${nav.portfolioId}`,
                  currency: 'USD',
                  settledBalance: nav.cashTotal,
                  projectedBalance: nav.cashTotal
                }));
            }
          }
        } catch (navErr) {
          console.warn('NAV fallback also failed:', navErr);
        }
      }

      // Final fallback: Use summary data if available
      if (cashData.length === 0 && summaryRes.data) {
        const portfoliosFromSummary = summaryRes.data.portfolios || [];
        if (portfoliosFromSummary.length > 0) {
          cashData = portfoliosFromSummary.map((p: any) => ({
            portfolioId: p.portfolioId,
            portfolioName: p.portfolioName,
            currency: 'USD',
            settledBalance: p.availableCash || p.netAvailable || 0,
            projectedBalance: p.availableCash || p.netAvailable || 0
          }));
        } else if (summaryRes.data.totalAvailableCash) {
          // If we have aggregate cash but no per-portfolio breakdown
          cashData = [{
            portfolioName: 'All Portfolios (Aggregate)',
            currency: 'USD',
            settledBalance: summaryRes.data.totalAvailableCash,
            projectedBalance: summaryRes.data.totalNetCash || summaryRes.data.totalAvailableCash
          }];
        }
      }

      setCashBalances(cashData);
    } catch (error) {
      console.error('Error fetching IBOR data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPortfolioId, asOfDate]);

  // Calculate stats from positions
  const stats = useMemo(() => {
    const activeSummary = iborViewMode === 'replay' ? replaySummary : (replaySummary || summary);
    const totalMV = positions.reduce((sum, p) => sum + (p.marketValue || 0), 0);
    const totalUnrealized = positions.reduce((sum, p) => sum + (p.unrealizedPnl || 0), 0);

    // Fallback to summary data if no positions
    if (positions.length === 0 && activeSummary) {
      const summaryMV = activeSummary.totalMarketValue || activeSummary.totalNetCash || 0;
      const availableCash = activeSummary.totalAvailableCash || 0;
      return {
        totalMV: summaryMV > 1000000000 ? `$${(summaryMV / 1000000000).toFixed(2)}B` : `$${(summaryMV / 1000000).toFixed(1)}M`,
        availableCash: `$${(availableCash / 1000000).toFixed(1)}M`,
        rawAvailableCash: availableCash,
        totalPositions: String(activeSummary.positionCount || activeSummary.totalPendingSettlements || 0),
        unrealizedPnl: '$0'
      };
    }

    return {
      totalMV: totalMV > 1000000000 ? `$${(totalMV / 1000000000).toFixed(2)}B` : `$${(totalMV / 1000000).toFixed(1)}M`,
      availableCash: `$${((activeSummary?.totalAvailableCash || 0) / 1000000).toFixed(1)}M`,
      rawAvailableCash: activeSummary?.totalAvailableCash || 0,
      totalPositions: String(positions.length),
      unrealizedPnl: totalUnrealized >= 0
        ? `+$${(totalUnrealized / 1000000).toFixed(2)}M`
        : `-$${(Math.abs(totalUnrealized) / 1000000).toFixed(2)}M`
    };
  }, [positions, summary, replaySummary, iborViewMode]);

  useEffect(() => {
    fetchIBORData();
  }, [fetchIBORData]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const formatAssetClass = (raw?: string): string => {
    if (!raw) return 'Unclassified';
    const map: Record<string, string> = {
      // --- Canonical taxonomy (post-V329) ---
      'LEVERAGED_LOAN': 'Leveraged Loan',
      'HY_BOND': 'HY Bond',
      'CLO_EQUITY': 'CLO Equity',
      'CLO_MEZZ': 'CLO Mezz',
      'CLO_SENIOR': 'CLO Senior',
      'PRIVATE_CREDIT': 'Private Credit',
      'PRIVATE_EQUITY': 'Private Equity',
      'RE_EQUITY': 'RE Equity',
      'RE_DEBT': 'RE Debt',
      'CASH': 'Cash',
      'INFRASTRUCTURE': 'Infrastructure',
      'REIT': 'REIT',
      'DISTRESSED_DEBT': 'Distressed',
      'CMBS_TRANCHE': 'CMBS',
      'PORTCO_EQUITY': 'Portco Equity',
      'PORTCO_DEBT': 'Portco Debt',
      // --- Legacy aliases (other tenants, pre-canonicalization) ---
      'REAL_ESTATE_EQUITY': 'RE Equity', 'REAL_ESTATE': 'RE Equity',
      'REAL_ESTATE_DEBT': 'RE Debt', 'REAL_ESTATE_FUND': 'Private Equity',
      'CLO_DEBT': 'CLO Debt', 'CLO': 'CLO',
      'CLO SENIOR': 'CLO Senior', 'CLO MEZZANINE': 'CLO Mezz',
      'LEVERAGED LOANS': 'Leveraged Loan',
      'SENIOR SECURED LOAN': 'Leveraged Loan',
      'SECOND LIEN LOAN': 'Leveraged Loan',
      'SENIOR SECURED': 'HY Bond', 'SENIOR UNSECURED': 'HY Bond', 'SENIOR SUBORDINATED': 'HY Bond',
      'CORPORATE BONDS': 'HY Bond',
      'FIXED_INCOME': 'Fixed Income', 'FIXED INCOME': 'Fixed Income',
      'TERM_LOAN': 'Term Loan', 'DISTRESSED': 'Distressed',
      'FX': 'FX', 'EQUITY': 'Equity', 'DERIVATIVE': 'Derivative', 'REPO': 'Repo',
      'FUND': 'Fund', 'CMBS': 'CMBS',
      'PRIVATE CREDIT LOAN': 'Private Credit',
    };
    const upper = raw.toUpperCase().trim();
    if (map[upper]) return map[upper];
    return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatCurrency = (value: number) => {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const isCashPosition = (position?: Position | null) =>
    !!position && (
      String(position.assetClass || '').toUpperCase() === 'CASH' ||
      String(position.cusip || '').toUpperCase().endsWith('_CASH')
    );

  const formatAcquisitionPrice = (lot: TaxLot) => {
    const price = Number(lot.acquisitionPrice || 0);
    if (!isCashPosition(selectedPosition)) {
      return `${price.toFixed(4)}`;
    }
    const normalizedCashPrice = price === 100 ? 1 : price;
    return normalizedCashPrice.toFixed(4);
  };

  const formatDate = (dateStr?: string | null) => {
    // Null-safe: synthesized lots (e.g. position-only fallback) can carry null
    // lot/settle dates. Also tolerate full ISO datetimes by slicing to the date.
    if (!dateStr) return '—';
    // Parse date string without timezone conversion (avoid UTC interpretation)
    const [year, month, day] = String(dateStr).slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return String(dateStr);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(String(dateStr).length === 16 ? `${dateStr}:00` : String(dateStr));
    if (Number.isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const applyReplayPreset = (preset: Exclude<ReplayPreset, null>) => {
    setIborViewMode('replay');
    setReplayPreset(preset);
    if (preset === 'worldThen') {
      setValidAsOf(current => current || asOfDate);
    } else if (preset === 'knownThen') {
      setValidAsOf(current => current || asOfDate);
    } else {
      setValidAsOf(getTenantToday());
      setKnowledgeAsOf(toDateTimeLocal(new Date()));
    }
  };

  const groupRowStyle = useCallback((row: any) => {
    if (!row?.__group) return undefined;
    const depth = Number(row.__depth || 0);
    const palette = [
      { background: 'rgba(14, 30, 52, 0.92)', border: 'rgba(56, 189, 248, 0.28)' },
      { background: 'rgba(11, 34, 36, 0.78)', border: 'rgba(45, 212, 191, 0.22)' },
      { background: 'rgba(31, 24, 54, 0.74)', border: 'rgba(167, 139, 250, 0.22)' },
    ][Math.min(depth, 2)];
    return {
      background: palette.background,
      borderTop: `1px solid ${palette.border}`,
      borderBottom: '1px solid rgba(15, 23, 42, 0.9)',
      fontWeight: 850,
    };
  }, []);

  const positionColumns = useMemo<ColDef<Position>[]>(() => [
    {
      field: 'securityName',
      headerName: 'Security',
      minWidth: 320,
      pinned: 'left',
      cellRenderer: (p: any) => {
        const row = p.data;
        if (row?.__group) {
          return (
            <GroupCell $depth={Number(row.__depth || 0)} $unclassified={row.__unclassified}>
              <GroupCaret>{row.__expanded ? '▾' : '▸'}</GroupCaret>
              <GroupTitle title={p.value}>{p.value}</GroupTitle>
              <GroupMeta>{row.__count} pos · {Number(row.__pct).toFixed(1)}% gross</GroupMeta>
            </GroupCell>
          );
        }
        return (
          <SecurityCell style={row?.__depth ? { paddingLeft: row.__depth * 18 } : undefined}>
            <SecurityName title={p.value || 'TOTAL'}>{p.value || 'TOTAL'}</SecurityName>
            {row?.portfolioName && (
              <PortfolioName title={row.portfolioName}>{row.portfolioName}</PortfolioName>
            )}
          </SecurityCell>
        );
      },
    },
    {
      field: 'cusip',
      headerName: 'CUSIP',
      width: 130,
      cellRenderer: (p: any) => {
        if (p.data?.__group) return '';
        return p.value ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/securities?cusip=${encodeURIComponent(p.value)}`);
            }}
            className="text-blue-400 hover:text-blue-300 hover:underline font-mono"
          >
            {p.value}
          </button>
        ) : '--';
      },
    },
    {
      field: 'assetClass',
      headerName: 'Asset Class',
      minWidth: 150,
      // Group and TOTAL rows aggregate across classes — blank, not "Unclassified".
      valueFormatter: p => ((p.data as any)?.__group || (p.data as any)?.securityName === 'TOTAL')
        ? '' : formatAssetClass(p.value),
    },
    {
      field: 'parAmount',
      headerName: 'Par Amount',
      width: 150,
      // FR-13: null on group/total rows means "mixed instrument families —
      // summing par would mislead"; render a dash instead of $0.00.
      valueFormatter: p => p.value == null && ((p.data as any)?.__group || (p.data as any)?.securityName === 'TOTAL')
        ? '—' : formatCurrency(Number(p.value || 0)),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } as any,
    },
    {
      field: 'unfundedCommitment',
      headerName: 'Unfunded',
      width: 145,
      valueFormatter: p => p.value == null && ((p.data as any)?.__group || (p.data as any)?.securityName === 'TOTAL')
        ? '—' : formatCurrency(Number(p.value || 0)),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: '#7dd3fc' } as any,
    },
    {
      field: 'marketValue',
      headerName: 'Market Value',
      width: 160,
      valueFormatter: p => formatCurrency(Number(p.value || 0)),
      cellStyle: { textAlign: 'right', fontWeight: '800' } as any,
    },
    {
      field: 'costBasis',
      headerName: 'Cost Basis',
      width: 150,
      valueFormatter: p => formatCurrency(Number(p.value || 0)),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } as any,
    },
    {
      field: 'unrealizedPnl',
      headerName: 'Unrealized P&L',
      width: 160,
      cellRenderer: (p: any) => {
        const value = Number(p.value || 0);
        return (
          <span className={cn("flex items-center justify-end gap-1 font-bold", value >= 0 ? "text-emerald-400" : "text-red-400")}>
            {value >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {formatCurrency(Math.abs(value))}
          </span>
        );
      },
    },
  ], [navigate]);

  const pivotActive = pivotDims.length > 0;

  // Grouped mode flattens the hierarchy into ordinary rows; sorting/filtering
  // would scramble group structure, so they are disabled while grouped.
  const registerColumns = useMemo<ColDef<Position>[]>(
    () => pivotActive
      ? positionColumns.map(column => {
        const baseCellStyle = column.cellStyle;
        return {
          ...column,
          sortable: false,
          filter: false,
          cellStyle: (params: any) => {
            const base = typeof baseCellStyle === 'function'
              ? baseCellStyle(params)
              : (baseCellStyle || {});
            return params.data?.__group
              ? { ...base, ...groupRowStyle(params.data) }
              : base;
          },
        };
      })
      : positionColumns,
    [groupRowStyle, positionColumns, pivotActive]
  );

  const displayRows = useMemo<any[]>(
    () => pivotActive
      ? buildPivotRows(positions, classifications, pivotDims, expandedGroups)
      : positions,
    [pivotActive, positions, classifications, pivotDims, expandedGroups]
  );

  const pinnedPositionTotals = useMemo<Position[]>(() => {
    // FR-13: par/quantity only total when the whole book is one instrument
    // family; a mixed book renders '—' (currency measures always total).
    const families = new Set(positions.map(p => p.assetClass || 'UNKNOWN'));
    const singleFamily = families.size <= 1;
    return [{
      id: -1,
      portfolioId: 0,
      portfolioName: 'All Portfolios',
      securityId: 0,
      securityName: 'TOTAL',
      cusip: '',
      assetClass: '',
      quantity: singleFamily ? positions.reduce((sum, p) => sum + Number(p.quantity || 0), 0) : (null as any),
      parAmount: singleFamily ? positions.reduce((sum, p) => sum + Number(p.parAmount || 0), 0) : (null as any),
      marketValue: positions.reduce((sum, p) => sum + Number(p.marketValue || 0), 0),
      costBasis: positions.reduce((sum, p) => sum + Number(p.costBasis || 0), 0),
      unrealizedPnl: positions.reduce((sum, p) => sum + Number(p.unrealizedPnl || 0), 0),
      realizedPnl: positions.reduce((sum, p) => sum + Number(p.realizedPnl || 0), 0),
      asOfDate: iborViewMode === 'replay' ? validAsOf : asOfDate,
    }];
  }, [asOfDate, validAsOf, iborViewMode, positions]);

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-blue-500" />
                IBOR Command
              </h1>
              <p className="text-slate-500 font-medium mt-1">Bi-temporal Investment Book of Record with tax lot management</p>
            </div>
            <div className="flex gap-3">
              {/* Portfolio Selector */}
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl">
                <AxiomFieldLabel style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Portfolio</AxiomFieldLabel>
                <AxiomSelect
                  value={selectedPortfolioId}
                  onChange={(e) => setSelectedPortfolioId(e.target.value)}
                  style={{ minWidth: 220 }}
                >
                  <option value="">All Portfolios</option>
                  {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
                </AxiomSelect>
              </div>
              <AxiomButton
                type="button"
                $variant="secondary"
                onClick={() => { fetchIBORData(); fetchPositions(); }}
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </AxiomButton>
            </div>
          </div>

          {/* Bi-Temporal Controls */}
          <GlassCard className="p-4">
            <div className="flex items-center gap-6 flex-wrap">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">IBOR Mode</label>
                <div className="flex bg-slate-950/50 rounded-xl p-1 border border-white/10">
                  <button
                    onClick={() => {
                      setIborViewMode('current');
                      setReplayPreset(null);
                    }}
                    className={cn(
                      "px-4 py-2 rounded-lg text-xs font-bold transition-all",
                      iborViewMode === 'current'
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    Current View
                  </button>
                  <button
                    onClick={() => setIborViewMode('replay')}
                    className={cn(
                      "px-4 py-2 rounded-lg text-xs font-bold transition-all",
                      iborViewMode === 'replay'
                        ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/20"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    Bi-Temporal Replay
                  </button>
                </div>
              </div>

              {/* Position Date Type Toggle */}
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{iborViewMode === 'replay' ? 'Basis' : 'Position Type'}</label>
                <div className="flex bg-slate-950/50 rounded-xl p-1 border border-white/10">
                  <button
                    onClick={() => setPositionDateType('trade')}
                    className={cn(
                      "px-4 py-2 rounded-lg text-xs font-bold transition-all",
                      positionDateType === 'trade'
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    <Clock className="w-3.5 h-3.5 inline mr-2" />
                    Trade Date
                  </button>
                  <button
                    onClick={() => setPositionDateType('settlement')}
                    className={cn(
                      "px-4 py-2 rounded-lg text-xs font-bold transition-all",
                      positionDateType === 'settlement'
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    <ShieldCheck className="w-3.5 h-3.5 inline mr-2" />
                    Settlement Date
                  </button>
                </div>
              </div>

              {iborViewMode === 'current' ? (
                <>
                  {/* As-of Date Selector */}
                  <div className="flex items-center gap-3">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">As-of Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <AxiomDateField
                        value={asOfDate}
                        onChange={(e) => setAsOfDate(e.target.value)}
                        style={{ paddingLeft: '2.35rem' }}
                      />
                    </div>
                  </div>

                  {/* Quick Date Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAsOfDate(getTenantToday())}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        setAsOfDate(addBusinessDays(getTenantToday(), -1));
                      }}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                    >
                      T-1
                    </button>
                    <button
                      onClick={() => {
                        const d = fromIso(getTenantToday());
                        d.setDate(d.getDate() - 7);
                        setAsOfDate(toIso(d));
                      }}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                    >
                      T-7
                    </button>
                    <button
                      onClick={() => {
                        const d = fromIso(getTenantToday());
                        d.setMonth(d.getMonth() - 1);
                        setAsOfDate(toIso(d));
                      }}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                    >
                      Month End
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Valid Time</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <AxiomDateField
                        value={validAsOf}
                        onChange={(e) => {
                          setValidAsOf(e.target.value);
                          setReplayPreset(null);
                        }}
                        style={{ paddingLeft: '2.35rem' }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Knowledge Time</label>
                    <AxiomInput
                      type="datetime-local"
                      value={knowledgeAsOf}
                      onChange={(e) => {
                        setKnowledgeAsOf(e.target.value);
                        setReplayPreset(null);
                      }}
                      style={{ width: 190 }}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => applyReplayPreset('worldThen')}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border",
                        replayPreset === 'worldThen' ? "bg-cyan-500/20 text-cyan-200 border-cyan-400/40" : "bg-slate-800 text-slate-400 hover:text-white border-white/5"
                      )}
                    >
                      World Then
                    </button>
                    <button
                      onClick={() => applyReplayPreset('knownThen')}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border",
                        replayPreset === 'knownThen' ? "bg-cyan-500/20 text-cyan-200 border-cyan-400/40" : "bg-slate-800 text-slate-400 hover:text-white border-white/5"
                      )}
                    >
                      Known Then
                    </button>
                    <button
                      onClick={() => applyReplayPreset('todayReconstructed')}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border",
                        replayPreset === 'todayReconstructed' ? "bg-cyan-500/20 text-cyan-200 border-cyan-400/40" : "bg-slate-800 text-slate-400 hover:text-white border-white/5"
                      )}
                    >
                      Today Reconstructed
                    </button>
                  </div>
                </>
              )}

              <div className="ml-auto text-[10px] text-slate-500">
                {iborViewMode === 'current' ? (
                  <>
                    <span className="text-blue-400 font-bold">{positionDateType === 'trade' ? 'Trade' : 'Settlement'}</span> positions as of{' '}
                    <span className="text-white font-bold">{formatDate(asOfDate)}</span>
                  </>
                ) : (
                  <>
                    <span className="text-cyan-300 font-bold">Valid</span>{' '}
                    <span className="text-white font-bold">{formatDate(validAsOf)}</span>
                    <span className="mx-2 text-slate-600">/</span>
                    <span className="text-cyan-300 font-bold">Known</span>{' '}
                    <span className="text-white font-bold">{formatDateTime(knowledgeAsOf)}</span>
                  </>
                )}
              </div>
            </div>

            {iborViewMode === 'replay' && (
              <div className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-500/5 px-4 py-3 text-xs text-slate-300">
                <span className="font-bold text-cyan-200">Replay mode:</span>{' '}
                official IBOR rows are selected where valid-time contains the world date and system-time contains the knowledge timestamp.
                Look-through exposure such as CLO collateral and CDS remains in Cross-Vehicle Exposure.
              </div>
            )}
          </GlassCard>

          {/* Stats Cards */}
          <AxiomMetricGrid>
            <AxiomMetricCard
              label="Total Market Value"
              value={stats.totalMV}
              icon={<DollarSign className="w-5 h-5" />}
              tone="cyan"
              subtext={iborViewMode === 'replay' ? 'Valid + knowledge time' : `${positionDateType === 'trade' ? 'Trade' : 'Settlement'} Date Basis · all rows`}
            />
            <AxiomMetricCard
              label="Available Cash"
              value={stats.availableCash}
              icon={<Zap className="w-5 h-5" />}
              tone={stats.rawAvailableCash < 0 ? "red" : "emerald"}
              subtext={iborViewMode === 'replay'
                ? 'Replay cash rows'
                : stats.rawAvailableCash < 0
                  ? 'Cash deficit — funding or overdraft action required'
                  : 'Ready for deployment'}
            />
            <AxiomMetricCard
              label="Total Positions"
              value={stats.totalPositions}
              icon={<Package className="w-5 h-5" />}
              tone="amber"
              subtext={iborViewMode === 'replay' ? 'Official IBOR rows' : 'All rows; quick search filters grid only'}
            />
            <AxiomMetricCard
              label="Unrealized P&L"
              value={stats.unrealizedPnl}
              icon={stats.unrealizedPnl.startsWith('+') ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              tone={stats.unrealizedPnl.startsWith('+') ? 'emerald' : 'red'}
              subtext="Mark-to-market · all rows"
            />
          </AxiomMetricGrid>
        </motion.div>

        {/* Positions Table with classification pivot grouping (FRD Phase 2). */}
        <InstitutionalDataGrid
          title="Governed position register"
          subtitle={pivotActive
            ? `Drill path: ${pivotDims.map(d => PIVOT_DIMENSION_LABELS[d]).join(' → ')} → Security. Group totals reconcile with the flat official register by construction.`
            : iborViewMode === 'replay'
              ? `Bi-temporal replay: valid ${formatDate(validAsOf)}, known at ${formatDateTime(knowledgeAsOf)}. Official IBOR rows only.`
              : `${positionDateType === 'trade' ? 'Trade-date' : 'Settlement-date'} positions as of ${formatDate(asOfDate)}. Row click opens tax lots and audit context.`}
          rowData={displayRows}
          columnDefs={registerColumns}
          gridId="ibor-position-register"
          height={620}
          rowHeight={56}
          headerHeight={42}
          sizeColumnsToFit={false}
          pinnedBottomRowData={pinnedPositionTotals}
          searchPlaceholder="Search security, CUSIP, portfolio, asset class..."
          initialQuickFilter={initialPositionQuery}
          emptyLabel={isLoadingPositions ? 'Loading positions...' : 'No positions found for the selected criteria'}
          getRowId={(row: any) => row.__group
            // Renderer-borne state (caret, count, pct) is part of the id: AG
            // Grid only re-renders cells whose VALUE changed, so group rows
            // kept stale counts/percentages when the portfolio filter changed
            // (options E2E finding 2026-08-04).
            ? `${row.__groupKey}#${row.__expanded ? 'open' : 'closed'}#${row.__count}#${Number(row.__pct).toFixed(1)}`
            : String(row.id ?? `${row.portfolioId}-${row.securityId}-${row.asOfDate ?? asOfDate}-${positionDateType}`)}
          toolbarExtra={(
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b' }}>
                  Group by
                </span>
                {(Object.keys(PIVOT_DIMENSION_LABELS) as PivotDimension[]).map(dim => {
                  const order = pivotDims.indexOf(dim);
                  return (
                    <PivotChip key={dim} $active={order >= 0} onClick={() => togglePivotDim(dim)}>
                      {order >= 0 && <sup>{order + 1}</sup>}
                      {PIVOT_DIMENSION_LABELS[dim]}
                    </PivotChip>
                  );
                })}
              </div>
              <AxiomStatusBadge $tone="cyan">{iborViewMode === 'replay' ? 'Replay' : (positionDateType === 'trade' ? 'T+0' : 'Settled')}</AxiomStatusBadge>
            </>
          )}
          onRowClicked={(row: any) => {
            if (row?.__group) {
              toggleGroup(row.__groupKey);
            } else if (row?.portfolioId && row?.securityId) {
              fetchTaxLots(row);
            }
          }}
        />


        {/* Summary Cards Row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <GlassCard className="xl:col-span-2 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-400" />
                Cash & Liquidity Positions
              </h3>
              <select className="bg-slate-950/50 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 min-w-[180px]">
                <option value="">All Currencies</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="pb-3 px-2">Portfolio</th>
                    <th className="pb-3 px-2">CCY</th>
                    <th className="pb-3 px-2 text-right">Settled Cash</th>
                    <th className="pb-3 px-2 text-right">Projected Cash</th>
                    <th className="pb-3 px-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {cashBalances.map((cb, i) => {
                    const settled = cb.settledBalance || 0;
                    const projected = cb.projectedBalance || 0;
                    const isNegative = settled < 0;
                    return (
                      <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <td className="py-3 px-2 font-semibold text-slate-200">{cb.portfolioName}</td>
                        <td className="py-3 px-2 font-mono text-slate-400">{cb.currency}</td>
                        <td className={cn("py-3 px-2 text-right font-bold", isNegative ? "text-red-400" : "text-white")}>
                          {isNegative ? '-' : ''}${Math.abs(settled).toLocaleString()}
                        </td>
                        <td className={cn("py-3 px-2 text-right font-bold", projected < 0 ? "text-red-400" : "text-blue-400")}>
                          {projected < 0 ? '-' : ''}${Math.abs(projected).toLocaleString()}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className={cn(
                            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                            isNegative ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"
                          )}>
                            {isNegative ? 'Overdraft' : 'Verified'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {cashBalances.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-500 italic">No cash positions found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>

          <div className="space-y-6">
            <GlassCard className="p-6">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Risk Metrics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Effective Dur</div>
                  <div className="text-lg font-bold text-white">{summary?.riskMetrics?.effectiveDuration || '4.28y'}</div>
                </div>
                <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">OAS Spread</div>
                  <div className="text-lg font-bold text-white">{summary?.riskMetrics?.oasSpread || '142 bps'}</div>
                </div>
                <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Avg Rating</div>
                  <div className="text-lg font-bold text-white">{summary?.riskMetrics?.avgRating || 'A-'}</div>
                </div>
                <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Yield to Worst</div>
                  <div className="text-lg font-bold text-white">{summary?.riskMetrics?.yieldToWorst || '5.12%'}</div>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </div>

      <ObjectDetailDrawer
        open={showLotModal && !!selectedPosition}
        title={selectedPosition?.securityName || 'Position'}
        subtitle={selectedPosition?.cusip}
        status={positionDateType === 'trade' ? 'Trade Date' : 'Settled'}
        assetClass={formatAssetClass(selectedPosition?.assetClass)}
        onClose={() => setShowLotModal(false)}
        identifiers={[
          { label: 'Portfolio', value: selectedPosition?.portfolioName || '--' },
          { label: 'CUSIP', value: selectedPosition?.cusip || '--' },
          { label: 'As-of Date', value: formatDate(iborViewMode === 'replay' ? validAsOf : asOfDate) },
          ...(iborViewMode === 'replay'
            ? [{ label: 'Known At', value: new Date(knowledgeAsOf).toLocaleString() }]
            : []),
        ]}
        metrics={[
          { label: 'Quantity', value: selectedPosition?.quantity?.toLocaleString() || '--', tone: 'slate' },
          { label: 'Cost Basis', value: formatCurrency(selectedPosition?.costBasis || 0), tone: 'cyan' },
          { label: 'Market Value', value: formatCurrency(selectedPosition?.marketValue || 0), tone: 'blue' },
          {
            label: 'Unrealized P&L',
            value: formatCurrency(selectedPosition?.unrealizedPnl || 0),
            tone: (selectedPosition?.unrealizedPnl || 0) >= 0 ? 'emerald' : 'red',
          },
        ]}
        sections={[
          {
            title: 'Tax Lots',
            eyebrow: `${taxLots.length} lot${taxLots.length === 1 ? '' : 's'}`,
            content: isLoadingLots ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <RefreshCcw className="w-5 h-5 animate-spin text-blue-400 mr-3" />
                Loading tax lots...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1040px]">
                  <thead>
                    <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/10">
                      <th className="pb-3 px-3">Lot #</th>
                      <th className="pb-3 px-3">Acquisition Date</th>
                      <th className="pb-3 px-3">Source</th>
                      <th className="pb-3 px-3">Status</th>
                      <th className="pb-3 px-3 text-right">Quantity</th>
                      <th className="pb-3 px-3 text-right">Acq. Price</th>
                      <th className="pb-3 px-3 text-right">Original Cost</th>
                      <th className="pb-3 px-3 text-right">Current Cost</th>
                      <th className="pb-3 px-3 text-right">Unrealized G/L</th>
                      <th className="pb-3 px-3 text-right">% G/L</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {taxLots.map((lot, i) => (
                      <tr key={lot.id || i} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <td className="py-3 px-3">
                          <div className="font-bold text-blue-400">#{lot.lotNumber}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{lot.lotReference}</div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="text-white">{formatDate(lot.lotDate)}</div>
                          <div className="text-[10px] text-slate-500">Settle: {formatDate(lot.settleDate)}</div>
                        </td>
                        <td className="py-3 px-3"><AxiomStatusBadge $tone="slate">{lot.sourceType}</AxiomStatusBadge></td>
                        <td className="py-3 px-3">
                          <AxiomStatusBadge $tone={lot.lotStatus === 'OPEN' ? 'emerald' : lot.lotStatus === 'PARTIAL' ? 'amber' : 'slate'}>
                            {lot.lotStatus}
                          </AxiomStatusBadge>
                        </td>
                        <td className="py-3 px-3 text-right font-mono text-white">{lot.currentQuantity?.toLocaleString()}</td>
                        <td className="py-3 px-3 text-right font-mono text-slate-400">{formatAcquisitionPrice(lot)}</td>
                        <td className="py-3 px-3 text-right font-mono text-slate-400">
                          {formatCurrency(lot.originalAcquisitionCost ?? lot.acquisitionCost ?? 0)}
                        </td>
                        <td className="py-3 px-3 text-right font-bold text-white">
                          {formatCurrency(lot.amortizedCost ?? lot.acquisitionCost ?? 0)}
                        </td>
                        <td className={cn("py-3 px-3 text-right font-bold", lot.unrealizedGainLoss >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {lot.unrealizedGainLoss >= 0 ? '+' : ''}{formatCurrency(lot.unrealizedGainLoss || 0)}
                        </td>
                        <td className={cn("py-3 px-3 text-right font-bold", lot.unrealizedGainLossPct >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {lot.unrealizedGainLossPct >= 0 ? '+' : ''}{lot.unrealizedGainLossPct?.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                    {taxLots.length === 0 && (
                      <tr>
                        <td colSpan={10} className="py-12 text-center text-slate-500 italic">No tax lots found for this position</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ),
          },
        ]}
      />
    </Container>
  );
};

export default IBORDashboardScreen;
